/**
 * GameState - Centralized game state management
 * Single source of truth for all game state with change events
 */

import { eventBus, GameEvents } from './EventBus.js';
import { STORAGE_KEYS } from './Constants.js';

class GameStateManager {
  constructor() {
    this.state = {
      // Core game stats
      fishCaught: 0,
      score: 0,

      // Fishing state
      isFishing: false,
      activeFishingHole: null,
      fishingRod: null,
      fishingLine: null,
      fishingFloat: null,
      fishBiting: false,
      fishingTimer: null,
      caughtFish: null,

      // Player state
      playerMoving: false,
      playerTargetPosition: null,
      playerMovementSpeed: 0.12,

      // Inventory (fish collection)
      inventory: {},

      // UI state
      showInventory: false,

      // Location state
      isInHouse: false,
      isTransitioning: false,

      // Environment state
      weather: 'cloudy',

      // Object references (set by modules)
      house: null,
      houseInterior: null,
      irisEffect: null,

      // Progression state (new)
      achievements: {},
      unlockedPonds: [0, 1, 2, 3], // Starting ponds
      rodLevel: 1,
      tutorialComplete: false,

      // Statistics (new)
      totalPlayTime: 0,
      consecutiveCatches: 0,
      pondsFished: new Set(),

      // Loading state
      gameStarted: false,
      loadingComplete: false
    };

    // Load saved state on initialization
    this.loadSavedState();
  }

  /**
   * Get a state value
   * @param {string} key - State key
   * @returns {*} State value
   */
  get(key) {
    return this.state[key];
  }

  /**
   * Get the entire state object
   * @returns {Object} The full state object
   */
  getState() {
    return this.state;
  }

  /**
   * Set multiple state values at once (alias for update)
   * @param {Object} updates - Object with key-value pairs to update
   */
  setState(updates) {
    this.update(updates);
  }

  /**
   * Set a state value and emit change event
   * @param {string} key - State key
   * @param {*} value - New value
   * @returns {*} The new value
   */
  set(key, value) {
    const oldValue = this.state[key];
    this.state[key] = value;

    // Emit state change event
    eventBus.emit(`state:${key}:changed`, {
      key,
      oldValue,
      newValue: value
    });

    // Emit specific events for important state changes
    this.emitSpecificEvents(key, oldValue, value);

    return value;
  }

  /**
   * Update multiple state values at once
   * @param {Object} updates - Object with key-value pairs to update
   */
  update(updates) {
    Object.entries(updates).forEach(([key, value]) => {
      this.set(key, value);
    });
  }

  /**
   * Emit specific events for important state changes
   */
  emitSpecificEvents(key, oldValue, newValue) {
    switch (key) {
      case 'score':
        eventBus.emit(GameEvents.SCORE_CHANGED, {
          oldScore: oldValue,
          newScore: newValue
        });
        break;

      case 'weather':
        eventBus.emit(GameEvents.WEATHER_CHANGED, {
          oldWeather: oldValue,
          newWeather: newValue
        });
        break;

      case 'isInHouse':
        if (newValue && !oldValue) {
          eventBus.emit(GameEvents.HOUSE_ENTERED);
        } else if (!newValue && oldValue) {
          eventBus.emit(GameEvents.HOUSE_EXITED);
        }
        break;

      case 'showInventory':
        if (newValue) {
          eventBus.emit(GameEvents.INVENTORY_OPENED);
        } else {
          eventBus.emit(GameEvents.INVENTORY_CLOSED);
        }
        break;

      case 'gameStarted':
        if (newValue && !oldValue) {
          eventBus.emit(GameEvents.GAME_STARTED);
        }
        break;
    }
  }

  /**
   * Initialize inventory with all fish as undiscovered
   * @param {Array} fishTypes - Array of fish type definitions
   */
  initializeInventory(fishTypes) {
    const inventory = {};
    fishTypes.forEach(fish => {
      inventory[fish.id] = {
        fish: fish,
        count: 0,
        discovered: false,
        firstCaught: null
      };
    });
    this.state.inventory = inventory;
  }

  /**
   * Add a caught fish to inventory
   * @param {Object} fish - Fish that was caught
   */
  addCaughtFish(fish) {
    const inventory = this.state.inventory;

    if (!inventory[fish.id]) {
      inventory[fish.id] = {
        fish: fish,
        count: 0,
        discovered: false,
        firstCaught: null
      };
    }

    const entry = inventory[fish.id];
    const isFirstCatch = !entry.discovered;

    entry.count++;
    entry.discovered = true;

    if (!entry.firstCaught) {
      entry.firstCaught = new Date().toLocaleDateString();
    }

    // Update stats
    this.set('fishCaught', this.state.fishCaught + 1);
    this.set('score', this.state.score + fish.points);
    this.set('consecutiveCatches', this.state.consecutiveCatches + 1);

    // Emit fish caught event
    eventBus.emit(GameEvents.FISH_CAUGHT, {
      fish,
      isFirstCatch,
      totalCaught: this.state.fishCaught,
      speciesCaught: entry.count
    });

    // Auto-save after catching fish
    this.saveState();

    return { isFirstCatch, totalCaught: entry.count };
  }

  /**
   * Reset consecutive catches (called on escape/failure)
   */
  resetConsecutiveCatches() {
    this.set('consecutiveCatches', 0);
  }

  /**
   * Get inventory statistics
   */
  getInventoryStats() {
    const inventory = this.state.inventory;
    const entries = Object.values(inventory);

    return {
      totalSpecies: entries.length,
      discoveredSpecies: entries.filter(e => e.discovered).length,
      totalCaught: this.state.fishCaught,
      totalScore: this.state.score
    };
  }

  /**
   * Check if a pond is unlocked
   * @param {number} pondIndex - Pond index
   */
  isPondUnlocked(pondIndex) {
    return this.state.unlockedPonds.includes(pondIndex);
  }

  /**
   * Unlock a pond
   * @param {number} pondIndex - Pond index to unlock
   */
  unlockPond(pondIndex) {
    if (!this.state.unlockedPonds.includes(pondIndex)) {
      this.state.unlockedPonds.push(pondIndex);
      eventBus.emit(GameEvents.AREA_UNLOCKED, { pondIndex });
      this.saveState();
    }
  }

  /**
   * Upgrade fishing rod
   * @param {number} level - New rod level
   */
  upgradeRod(level) {
    if (level > this.state.rodLevel) {
      this.set('rodLevel', level);
      eventBus.emit(GameEvents.ROD_UPGRADED, { level });
      this.saveState();
    }
  }

  /**
   * Save state to localStorage
   */
  saveState() {
    try {
      const saveData = {
        version: 1,
        lastSaved: new Date().toISOString(),
        progress: {
          fishCaught: this.state.fishCaught,
          score: this.state.score,
          totalPlayTime: this.state.totalPlayTime,
          consecutiveCatches: this.state.consecutiveCatches
        },
        inventory: {},
        progression: {
          unlockedPonds: this.state.unlockedPonds,
          rodLevel: this.state.rodLevel,
          tutorialComplete: this.state.tutorialComplete
        }
      };

      // Save inventory (only essential data, not fish objects)
      Object.entries(this.state.inventory).forEach(([id, entry]) => {
        if (entry.discovered) {
          saveData.inventory[id] = {
            count: entry.count,
            discovered: entry.discovered,
            firstCaught: entry.firstCaught
          };
        }
      });

      localStorage.setItem(STORAGE_KEYS.GAME_SAVE, JSON.stringify(saveData));
    } catch (error) {
      console.warn('Failed to save game state:', error);
    }
  }

  /**
   * Load saved state from localStorage
   */
  loadSavedState() {
    try {
      const savedData = localStorage.getItem(STORAGE_KEYS.GAME_SAVE);
      if (!savedData) return;

      const data = JSON.parse(savedData);

      // Load progress
      if (data.progress) {
        this.state.fishCaught = data.progress.fishCaught || 0;
        this.state.score = data.progress.score || 0;
        this.state.totalPlayTime = data.progress.totalPlayTime || 0;
      }

      // Load progression
      if (data.progression) {
        this.state.unlockedPonds = data.progression.unlockedPonds || [0, 1, 2, 3];
        this.state.rodLevel = data.progression.rodLevel || 1;
        this.state.tutorialComplete = data.progression.tutorialComplete || false;
      }

      // Inventory will be merged when fish types are initialized
      this._savedInventory = data.inventory || {};

      console.log('Game state loaded from save');
    } catch (error) {
      console.warn('Failed to load saved game state:', error);
    }
  }

  /**
   * Merge saved inventory data with fish types
   * Called after fish types are loaded
   */
  mergeSavedInventory() {
    if (!this._savedInventory) return;

    Object.entries(this._savedInventory).forEach(([id, savedEntry]) => {
      if (this.state.inventory[id]) {
        this.state.inventory[id].count = savedEntry.count;
        this.state.inventory[id].discovered = savedEntry.discovered;
        this.state.inventory[id].firstCaught = savedEntry.firstCaught;
      }
    });

    delete this._savedInventory;
  }

  /**
   * Reset game (clear save)
   */
  resetGame() {
    localStorage.removeItem(STORAGE_KEYS.GAME_SAVE);
    localStorage.removeItem(STORAGE_KEYS.ACHIEVEMENTS);
    localStorage.removeItem(STORAGE_KEYS.PROGRESSION);

    // Reset state to defaults
    this.state.fishCaught = 0;
    this.state.score = 0;
    this.state.unlockedPonds = [0, 1, 2, 3];
    this.state.rodLevel = 1;
    this.state.tutorialComplete = false;

    // Reset inventory
    Object.values(this.state.inventory).forEach(entry => {
      entry.count = 0;
      entry.discovered = false;
      entry.firstCaught = null;
    });
  }
}

// Singleton instance
export const gameState = new GameStateManager();

export default GameStateManager;
